import { FileOpener } from "@capacitor-community/file-opener";
import { App } from "@capacitor/app";
import { Clipboard } from "@capacitor/clipboard";
import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Haptics, ImpactStyle } from "@capacitor/haptics";
import { InAppBrowser } from "@capacitor/inappbrowser";
import { Preferences } from "@capacitor/preferences";
import { AppLauncher } from "@capacitor/app-launcher";
import {
  APPLINKS_DEMO_URL,
  APPLINKS_DEV_URL,
  APPLINKS_TEST_URL,
  CountryCodesWithNames,
  DEMO_SUBDOMAIN,
  DEV_SUBDOMAIN,
  EventrDate,
  ICategory,
  ROCKETCHAT_DEMO_DOMAIN,
  ROCKETCHAT_DEV_DOMAIN,
  ROCKETCHAT_TEST_DOMAIN,
  TEST_SUBDOMAIN,
  TOrgRole,
  V2,
  checkClaimsWeb,
  getDocumentDisplayName,
  spError,
  spErrorTags,
} from "@eventr/common";
import { ColoursObj } from "@eventr/common-storybook";
import { onSkin } from "./state/themeState/skin-colour";
import * as Sentry from "@sentry/capacitor";
import { compareVersions } from "compare-versions";
import { ISection } from "./data/Data";
import { AppLog, useLogsStore } from "./feat/developerMenu/logs.store";
import { useGuestStore } from "./feat/guest/guest.store";
import i18n from "./i18n/i18next";
import { bs } from "./state/bound.store";
import { IItinerary } from "./state/interfaces";
import { defaultAddSnack } from "./state/overlayState/overlayContext";
import { useOverlayStore } from "./state/overlayState/overlay.store";
import { reactAppStage } from "./utils/reactAppStage";
import { chatBackend, spchatUrl, spchatWebsocket } from "./feat/rocketchat/chatBackend";
import { openFileViewer } from "./feat/fileViewer/open-file-viewer";

/** The cache hands back a Blob, or on some platforms the file's base64 contents. */
const asBlob = (data: Blob | string): Blob =>
  typeof data === "string" ? new Blob([Uint8Array.from(atob(data), (c) => c.charCodeAt(0))]) : data;

const ID_PREFIX = "!##!";

export const getMobileOperatingSystem = () => {
  const userAgent = navigator.userAgent;

  if (/android/i.test(userAgent)) {
    return "Android";
  }

  // iOS detection from: http://stackoverflow.com/a/9039885/177710
  if (/iPad|iPhone|iPod/.test(userAgent)) {
    return "iOS";
  }

  return "unknown";
};

export const compareBuildVersion = (currentBuildVersion: string, versionLock: string) => {
  try {
    if (Capacitor.isNativePlatform() && currentBuildVersion) {
      return compareVersions(currentBuildVersion, !versionLock ? "100.0.0" : versionLock) !== -1;
    } else if (!currentBuildVersion) {
      // Version not yet resolved: assume it meets the lock until it reports otherwise.
      return true;
    } else {
      return true;
    }
  } catch {
    console.error("Build Version feature is currently not available");
  }
};

const splitValueID = (valueAndID: string) => {
  if (!valueAndID) return { value: "", ID: "" };
  const stringValue = valueAndID.toString();
  const split = stringValue.split(ID_PREFIX);
  return { value: split[0], ID: split[1] };
};

export const getValue = (valueAndID: string) => {
  const { value } = splitValueID(valueAndID);
  return value;
};
export const capitalizeFirstLetter = (val: string) => {
  return val.charAt(0).toUpperCase() + val.slice(1);
};

export const getRandomString = (length: number) => {
  const randomChars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < length; i++) {
    result += randomChars.charAt(Math.floor(Math.random() * randomChars.length));
  }
  return result;
};

export const bytesToBase64 = (bytes: Uint8Array) => {
  const binString = Array.from(bytes, (x) => String.fromCodePoint(x)).join("");
  return btoa(binString);
};

export const getRocketChatWebsocket = (org: string) => {
  if (chatBackend() === "spchat") return spchatWebsocket();
  const stage = reactAppStage();
  if (stage === "demo") return `wss://${org}.${ROCKETCHAT_DEMO_DOMAIN}/websocket`;

  if (stage === "test") return `wss://${org}.${ROCKETCHAT_TEST_DOMAIN}/websocket`;

  if (stage === "dev") return `wss://${org}.${ROCKETCHAT_DEV_DOMAIN}/websocket`;

  if (stage === "local") return `ws://chat-${org}.localhost:3500/websocket`;

  return `wss://${org}.${ROCKETCHAT_DEV_DOMAIN}/websocket`;
};

export const getRocketChatUrl = (org: string) => {
  if (chatBackend() === "spchat") return spchatUrl();
  const stage = reactAppStage();
  if (stage === "demo") return `https://${org}.${ROCKETCHAT_DEMO_DOMAIN}`;

  if (stage === "test") return `https://${org}.${ROCKETCHAT_TEST_DOMAIN}`;
  if (stage === "dev") return `https://${org}.${ROCKETCHAT_DEV_DOMAIN}`;

  if (stage === "local") return `http://chat-${org}.localhost:3500`;

  return `https://${org}.${ROCKETCHAT_DEMO_DOMAIN}`;
};

/**
 * The applinks static host for this stage: the universal-link domain, which also serves the OTA
 * release configs. `local` has no applinks host of its own, so it falls back to this origin.
 *
 * Not where a browser user should be sent back to — on desktop that host answers every path with
 * the app-store download page. Use `emailReturnUrl` for anything that goes into an email.
 */
export const applinksUrl = (url: string) => {
  const stage = reactAppStage();
  switch (stage) {
    case "demo":
      return `${APPLINKS_DEMO_URL}/${url}`;
    case "test":
      return `${APPLINKS_TEST_URL}/${url}`;
    case "dev":
      return `${APPLINKS_DEV_URL}/${url}`;
    case "local":
      return window.location.origin + "/" + url;
    default:
      return `${APPLINKS_DEMO_URL}/${url}`;
  }
};

/**
 * Where an emailed link should send the recipient back to.
 *
 * The browser build knows the recipient is in a browser, so it names its own origin and the link
 * reopens the app they are already using. A native build has no origin worth naming and hands the
 * return to applinks, whose page deep-links into the app — but that same page is an app-store
 * prompt on desktop, which is where a browser user's journey would end.
 *
 * `url` must be slash-free: the base is joined with one here.
 */
export const emailReturnUrl = (url: string) =>
  isTeamForWeb ? `${window.location.origin}/${url}` : applinksUrl(url);

export const getSubdomain = () => {
  const stage = reactAppStage();
  switch (stage) {
    case "demo":
      return `${DEMO_SUBDOMAIN}`;
    case "test":
      return `${TEST_SUBDOMAIN}`;
    case "dev":
      return `${DEV_SUBDOMAIN}`;
    case "local":
      return ``;
    default:
      return `${DEMO_SUBDOMAIN}`;
  }
};

export const sectionInfoToEmptyV2 = (sectionInfo: ISection): V2 => {
  return {
    data: {},
    sectionName: sectionInfo?.name,
    renderer: sectionInfo?.properties?.renderer as any,
    sectionID: sectionInfo?.id,
    rendererOptions: sectionInfo?.properties?.rendererOptions as any,
    sectionDisplayName: sectionInfo?.properties?.sectionDisplayName ?? "",
    sectionDisplayNameTeams: sectionInfo?.properties?.sectionDisplayNameTeams ?? "",
    RCChannels: [],
  };
};

//Updates local version of itinerary with new section item data
// it used to have an updated version of the itinerary before useEffect kicks in to update from the server
export const updateSection = (itinData: IItinerary, sectionItem: any, sectionId: number) => {
  const newItinData = { ...itinData };
  const sectionIndex = newItinData.v2?.findIndex((section) => section.sectionID === sectionId);
  if (sectionIndex === undefined || sectionIndex === -1) {
    return newItinData;
  }
  const keys = Object.keys(newItinData.v2?.[sectionIndex]?.data || {});
  const firstKey = keys.length > 0 ? keys[0] : undefined;
  if (firstKey === undefined) {
    return newItinData;
  }
  const items = newItinData.v2![sectionIndex]!.data[firstKey];
  const found = items.find((item: any, index: number) => {
    if (item._id === sectionItem._id) {
      items[index] = sectionItem;
      return true;
    }
    return false;
  });
  if (!found) {
    spErrorTags(`updateSection: item not found in top-level data, pushed as new entry: ${sectionItem?._id} (section ${sectionId})`, ["SAVE"]);
    items.push(sectionItem);
  }
  return newItinData;
};

/**
 * Filter out pending items. (for use within filter func)
 */
export function filterOutPendingTag(item: any): boolean {
  if (!item) {
    /// Error?
    return false;
  }

  if (item._pending) {
    return false;
  } else {
    return true;
  }
}
/**
 * Filter out items that are copied.
 */
export function filterOutCopiedItems(item: any): boolean {
  if (item._og_code) {
    return false;
  }
  return true;
}
export const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const Utils = {
  getToken: () => {
    return bs().IDtoken;
  },

  setToken: async (accessToken: string) => {
    localStorage.setItem("accessToken", accessToken);
    Preferences.set({ key: "accessToken", value: accessToken });
  },

  setRefreshToken: async (refreshToken: string) => {
    localStorage.setItem("refreshToken", refreshToken);
    Preferences.set({ key: "refreshToken", value: refreshToken });
  },

  getVersion: async () => {
    if (!Capacitor.isNativePlatform()) return "webView";
    console.log(await App.getInfo());

    return (await App.getInfo()).version;
  },

  logoutClient: async () => {
    return await Preferences.clear();
  },

  hapticFeedback: async (feedbackType: "light" | "medium" | "heavy" | "selectionClick" | "vibrate") => {
    if (!Capacitor.isNativePlatform()) return;
    const feedbackmap = {
      light: ImpactStyle.Light,
      medium: ImpactStyle.Medium,
      heavy: ImpactStyle.Heavy,
    };
    if (feedbackType === "selectionClick") return await Haptics.selectionStart();
    if (feedbackType === "vibrate") return await Haptics.vibrate();
    return await Haptics.impact({
      style: feedbackmap[feedbackType],
    });
  },

  pushLog: async (log: AppLog) => {
    useLogsStore.getState().pushLog(log);
  },

  downloadAndOpenFile: async (url: any) => {
    // On the web the file opens in a viewer tab, opened here before the first await.
    const viewer = Capacitor.isNativePlatform() ? undefined : openFileViewer();
    const showInViewer = (blob: Blob | string) => viewer?.show(asBlob(blob), getDocumentDisplayName(url));
    try {
      // await requestFilePermission()
      const cacheFile = await getFileFromCache(url);
      if (cacheFile) {
        // const file = await moveFromCacheToDocuments(cacheFile.path)
        if (Capacitor.isNativePlatform()) {
          FileOpener.open({ filePath: cacheFile.path ?? "" });
        } else {
          showInViewer(cacheFile.blob);
        }
      } else {
        try {
          useOverlayStore.getState().setLoading(true);
          const newCacheFile = await CacheFile(url, true);
          // const file = await moveFromCacheToDocuments(cachefile.path)
          useOverlayStore.getState().setLoading(false);
          if (Capacitor.isNativePlatform() && newCacheFile) {
            const fullPath = await Filesystem.getUri({
              directory: Directory.Cache,
              path: newCacheFile.path,
            });
            FileOpener.open({ filePath: fullPath?.uri ?? "" });
          } else {
            showInViewer(newCacheFile.blob);
          }
        } catch (error) {
          spErrorTags("downloadAndOpenFile", ["FILES"], error);
          useOverlayStore.getState().setLoading(false);
          viewer?.fail();
        }
      }
    } catch (error) {
      spErrorTags("downloadAndOpenFile", ["FILES"], error);
      useOverlayStore.getState().setLoading(false);
      viewer?.fail();
    }
  },

  getReactVersion: () => {
    return import.meta.env.VITE_APP_VERSION;
  },
};

interface ICacheFileResult {
  path: string;
  blob: Blob | string;
}
/**
 * Downloads a file from the specified URL and saves it in the cache directory.
 *
 * @param url - The URL of the file to be downloaded.
 * @returns A promise that resolves to the downloaded file.
 */
export const CacheFile = async (url: string, updateCache?: boolean): Promise<ICacheFileResult> => {
  if (!url) {
    return { path: "", blob: "" };
  }
  const filePath = url.replace(/^(?:\/\/|[^/]+)*\//, "");
  if (updateCache) {
    const file = await downloadToCache(url);

    return { path: filePath, blob: file?.blob || "" };
  }
  // check if file is already in cache
  try {
    const cacheImage = await Filesystem.readFile({
      path: filePath,
      directory: Directory.Cache,
    });
    if (cacheImage.data) {
      return { path: filePath, blob: cacheImage.data };
    }
  } catch {
    // file not in cache
    const file = await downloadToCache(url);
    if (file) {
      return { path: filePath, blob: file?.blob || "" };
    }
  }
  // Ensure a default return in case all other paths fail
  return { path: "", blob: "" };
};

const downloadToCache = async (url: string) => {
  const filePath = url.replace(/^(?:\/\/|[^/]+)*\//, "");
  // recursive flag does not work with android  need to check if directroy exists and create it

  if (Capacitor.isNativePlatform()) {
    const fileDir = filePath.split("/").slice(0, -1).join("/");
    await createDirectoryIfNotExists(fileDir);
  }
  try {
    const file = await Filesystem.downloadFile({
      url,
      directory: Directory.Cache,
      headers: {
        Authorization: `Bearer ${await Utils.getToken()}`,
        "x-sp-guest-jwt": getGuestJWT() ?? "",
      },
      path: filePath,
      recursive: true,
    });

    return file;
  } catch (error: any) {
    spErrorTags("failed to download file: " + url, ["FILES"], error);
    spErrorTags(error?.message ?? "", ["FILES"]);
  }
};

const getGuestJWT = () => {
  if (useGuestStore.getState().isGuestMode) {
    return useGuestStore.getState().guestData?.guest_token;
  }
  return null;
};

// const moveFromCacheToDocuments = async (path: string) => {
//   try {
//     const cacheImage = await Filesystem.readFile({
//       path: path,
//       directory: Directory.Cache,
//     });
//     if (cacheImage.data) {
//       const fileName = path.split("/").pop();
//       return Filesystem.writeFile({
//         path: fileName,
//         data: cacheImage.data,
//         directory: Directory.Documents,
//       });
//     }
//   } catch (error) {
//     spErrorTags("moveFromCacheToDocuments", ["FILES"], error);
//   }
// };

const getFileFromCache = async (url: string): Promise<ICacheFileResult | undefined> => {
  const filePath = url.replace(/^(?:\/\/|[^/]+)*\//, "");
  try {
    const cacheImage = await Filesystem.readFile({
      path: filePath,
      directory: Directory.Cache,
    });

    if (cacheImage.data) {
      const fullPath = await Filesystem.getUri({
        directory: Directory.Cache,
        path: filePath,
      });
      return { path: fullPath.uri, blob: cacheImage.data };
    }
    return undefined;
  } catch (error) {
    spErrorTags("getFileFromCache", ["FILES"], error);
    return undefined;
  }
};

// const requestFilePermission = async () => {
//   try {
//     if (!Capacitor.isNativePlatform()) return;
//     const currentPerm = await Filesystem.checkPermissions();
//     if (currentPerm.publicStorage === "granted") return currentPerm;
//     const permission = await Filesystem.requestPermissions();
//     return permission;
//   } catch (error) {
//     spErrorTags("requestFilePermission", ["FILES"], error);
//   }
// };

const createDirectoryIfNotExists = async (path: string) => {
  try {
    await Filesystem.readdir({
      path,
      directory: Directory.Cache,
    });
  } catch {
    try {
      await Filesystem.mkdir({
        path,
        directory: Directory.Cache,
        recursive: true, // This will create directories along the path if they do not exist
      });
    } catch (error) {
      spErrorTags("createDirectoryIfNotExists", ["FILES"], error);
    }
  }
};

export const enableChat = () => {
  if (!checkClaimsWeb(["/"], ["chatAccess"])) {
    return false;
  }
  try {
    if (!bs().orgConfig?.disableChat) {
      /// Will be true if to disable
      return true;
    } else {
      /// if false or undifined
      return false;
    }
  } catch {
    /// Default on.
    return true;
  }
};

/**
 * Both claim-derived tab flags, from one read of the claims.
 *
 * They gate the native tab bar's Chat and Team tabs, and the bar reflows when either one
 * moves. Derived in separate store writes they land a reflow apart. That is how a first
 * organisation could finish building and still leave the Team tab off the bar until the
 * app was relaunched, because the reflow that `chatEnabled` triggered read a
 * `teamPageEnabled` that was still false. Callers spread this into a single `setState` so
 * the pair moves together.
 */
export const claimDerivedTabFlags = () => ({
  chatEnabled: enableChat(),
  teamPageEnabled: checkClaimsWeb(["/"], ["viewTeamPage"]),
});

export const getTimezoneOfItinerary = (itinData: IItinerary) => {
  try {
    let timezone = itinData?.v2?.[0]?.data?.OSMAddress?.timeZone;

    /// to deal with legacy cc set
    if (!timezone && itinData?.v2?.[0]?.data?.OSMAddress?.timeZone) {
      const selectedCountry = CountryCodesWithNames.find((countryObj) => {
        return countryObj.id === itinData?.v2?.[0]?.data?.OSMAddress?.timeZone;
      });
      if (selectedCountry) {
        timezone = selectedCountry.timezones[0]; /// take first
      }
    }

    return timezone;
  } catch {
    return undefined;
  }
};

export function mapRoleNamesWithRole(roleName: string, roleMap: TOrgRole[]) {
  if (!roleMap) return roleName;
  const roleMapSingle = roleMap.find((roleObj) => {
    return roleObj.role === roleName;
  });
  if (roleMapSingle) {
    return roleMapSingle.name;
  } else {
    return roleName;
  }
}

/**
 * Copies the provided content to the clipboard.
 *
 * @param contentConst - The content to be copied to the clipboard.
 * @param version - An object containing the versions of Flutter and React used. This is obtained from useAuthContext().
 * @param snackBar - An instance of the snackbar hook.
 *
 * @returns void
 *
 * @async
 */
export async function copyToClipboard(
  contentConst: string,
  version: { buildVersion?: string; reactVersion?: string }, //version is taken from useAuthContext()
) {
  // Check if the content is not empty
  if (contentConst && contentConst !== "") {
    try {
      // Check if the platform is native
      if (Capacitor.isNativePlatform()) {
        // Check if the Flutter version is greater than or equal to 0.9.24
        if (compareBuildVersion(version.buildVersion ?? "", "0.9.24")) {
          // Write the content to the clipboard
          Clipboard.write({
            string: String(contentConst),
          });
          // If the platform is iOS, show a success snackbar
          if (Capacitor.getPlatform() === "ios") {
            defaultAddSnack(i18n.t("snack.copied-clipboard"), "success");
          }
        } else {
          return;
        }
      } else {
        // If the platform is not native
        if (navigator.clipboard) {
          // Write the content to the clipboard
          await navigator.clipboard.writeText(String(contentConst));
          // Show a success snackbar
          defaultAddSnack(i18n.t("snack.copied-clipboard"), "success");
        } else {
          // If the clipboard is not available, show an error snackbar
          defaultAddSnack(i18n.t("snack.not-secure-context"), "error");
        }
      }
    } catch (error) {
      spError(error);
    }
  }
}

/**
 * i needed a quick function to get all day of a datestring, eg set it to the end or start of day.
 */
export function getAllDayOfDate(dateTimeString: string, toggle: "start" | "end") {
  const newEventrDate = new EventrDate(dateTimeString);
  switch (toggle) {
    case "start":
      newEventrDate.setFromObject({ hour: 0, minute: 0, millisecond: 1 });
      return newEventrDate.dateTimeString;
    case "end":
      newEventrDate.setFromObject({
        hour: 23,
        minute: 59,
        millisecond: 999,
      });
      return newEventrDate.dateTimeString;
  }
}

/**
 * Returns react branch version.
 */
export const reactBranch = () => {
  if (import.meta.env.VITE_APP_VERSION) {
    return import.meta.env.VITE_APP_VERSION.split("-")[1];
  } else {
    /// fallback is react app stage.
    return reactAppStage() ?? "demo";
  }
};

/**
 * Colour for dev Overlay for things...
 * from local storage
 */
export const reactAppStageColour = () => {
  if (!localStorage.getItem("REACT_APP_STAGE")) {
    return "red";
  }
  return getColourFromStage(reactAppStage());
};

/**
 * Colour for dev Overlay for things...
 * from local storage
 */
export const reactAppBranchColour = () => {
  if (!reactBranch()) {
    return "red";
  }
  return getColourFromStage(reactBranch());
};

export const isWebPreview = import.meta.env.VITE_APP_WEB === "true";

/** The hosted browser build: every route and sign-in, but no app store to buy through. */
export const isTeamForWeb = import.meta.env.VITE_APP_TEAM_WEB === "true";

const getColourFromStage = (stage: string) => {
  switch (stage) {
    case "dev":
      return "#18B1AB";
    case "test":
      return "#F3C724";
    case "prod":
      return "#4F3473";
    case "local":
      return "#4F3473";
    case "capacitor":
      return "#4F3473";
    default:
      return "white";
  }
};

export const isDebugUser = () => {
  if (isWebPreview) return false;
  if (isDebugModeEnabled(bs()?.profile?.debugFlag, 1)) {
    return true;
  }
  return false;
};
export const captureSentryMessage = (message: string, captureLevel?: Sentry.SeverityLevel) => {
  if (isWebPreview) return;
  if (isDebugModeEnabled(bs().profile?.debugFlag, 1)) {
    Sentry.captureMessage(message, {
      level: captureLevel ?? "info",
      fingerprint: [bs().profile?.email ?? "NO_USER"],
    });
  }
};

const isDebugModeEnabled = (debugFlag: number | undefined, flagType: number) => {
  if (debugFlag === undefined) {
    return false;
  }

  return (debugFlag & flagType) !== 0;
};

/**
 * device detect for the name of device pref. needs the func passed in as meant to be ran within a comp...
 */
export function getDevicePrefName(deviceDetect: (userAgent: string | undefined) => any): string {
  const deviceInfo = deviceDetect(navigator.userAgent);
  const vendor = deviceInfo.vendor || "WebView";
  const model = deviceInfo.model || "WebView";

  return `${vendor}-${model}`;
}

export const openBrowser = (url: string) => {
  let tmpUrl = url;
  if (!url.includes("https://") && !url.includes("http://")) {
    tmpUrl = "https://" + tmpUrl;
  }
  if (Capacitor.isNativePlatform()) {
    InAppBrowser.openInExternalBrowser({
      url: tmpUrl,
    });
  } else {
    window.open(tmpUrl);
  }
};

const STORE_LINKS = {
  appleMaps: {
    ios: "https://apps.apple.com/app/id915056765",
  },
  googleMaps: {
    ios: "https://apps.apple.com/app/id585027354",
    android: "https://play.google.com/store/apps/details?id=com.google.android.apps.maps",
  },
  waze: {
    ios: "https://apps.apple.com/app/id323229106",
    android: "https://play.google.com/store/apps/details?id=com.waze",
  },
} as const;

async function openAppOrFallback(scheme: string, appUrl: string, storeUrl: string) {
  const { value: canOpenAppUrl } = await AppLauncher.canOpenUrl({ url: scheme });

  if (canOpenAppUrl) {
    await AppLauncher.openUrl({ url: appUrl });
  } else {
    const { value: canOpenStoreUrl } = await AppLauncher.canOpenUrl({ url: storeUrl });

    if (canOpenStoreUrl) {
      await AppLauncher.openUrl({ url: storeUrl });
    } else {
      defaultAddSnack(i18n.t("snack.try-later"), "error");
    }
  }
}

export async function openMapApp(app: keyof typeof STORE_LINKS, address: string) {
  const encodedAddress = encodeURIComponent(address);
  const platform = Capacitor.getPlatform();
  const isNative = Capacitor.isNativePlatform();

  const isIOS = platform === "ios";
  const isAndroid = platform === "android";

  switch (app) {
    case "appleMaps": {
      if (isNative) {
        if (isIOS) {
          await openAppOrFallback("maps://", `maps://?q=${encodedAddress}`, STORE_LINKS.appleMaps.ios);
        }
      } else {
        await openAppOrFallback(
          "maps://",
          `maps://?q=${encodedAddress}`,
          `https://maps.apple.com/?q=${encodedAddress}`,
        );
      }
      break;
    }

    case "googleMaps": {
      if (isNative) {
        if (isIOS) {
          await openAppOrFallback(
            "comgooglemaps://",
            `comgooglemaps://?q=${encodedAddress}`,
            STORE_LINKS.googleMaps.ios,
          );
        } else if (isAndroid) {
          await openAppOrFallback(
            "https://maps.google.com",
            `https://maps.google.com/?q=${encodedAddress}`,
            STORE_LINKS.googleMaps.android,
          );
        }
      } else {
        await AppLauncher.openUrl({ url: `https://www.google.com/maps/search/?api=1&query=${encodedAddress}` });
      }
      break;
    }

    case "waze": {
      if (isNative && (isIOS || isAndroid)) {
        const storeUrl = isIOS ? STORE_LINKS.waze.ios : STORE_LINKS.waze.android;
        await openAppOrFallback("waze://", `waze://?q=${encodedAddress}&navigate=yes`, storeUrl);
      } else {
        await AppLauncher.openUrl({ url: `https://www.waze.com/live-map?query=${encodedAddress}` });
      }
      break;
    }
  }
}

export const getMinutesFromUnixTimestamp = (timestamp: number): number => {
  return Math.floor(timestamp / 60000);
};

export const getDelayPromise = (delay: number) => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(true);
    }, delay);
  });
};

export const cleanRCName = (name: string) => {
  return name
    .replace(/#/g, "")
    .replace(/_SPRC_.*$/, "")
    .replace(/_/g, " ");
};

/**
 * Determines whether the provided Eventr date string represents a date in the past.
 *
 * @param date - An optional ISO date string to check.
 * @returns `true` if the date is in the past, `false` otherwise or if no date is provided.
 */
export const isIEventrDatePast = (date?: string) => {
  if (!date) return false;
  const eventrDate = new EventrDate(date);
  return eventrDate.getUnixMs < new EventrDate().getUnixMs;
};

export function getCategoryProperties(SectionItemCategory: string | undefined): ICategory | undefined {
  const allOrgCategories = bs().orgConfig.categories;
  if (!allOrgCategories || !SectionItemCategory) {
    return undefined;
  }
  return allOrgCategories.find((cat) => cat.pub_id === SectionItemCategory);
}

export function getCategoryColours(past: boolean, category: ICategory | undefined, fallbackColour?: string) {
  const categoryColour = onSkin(category?.colour);

  const iconColour = past ? ColoursObj.shades.shade2 : (fallbackColour ?? ColoursObj.core.secondary);
  const iconCatColour = past ? iconColour : categoryColour ? categoryColour : iconColour;
  return iconCatColour;
}
